<!--
  Modo predictivo de anomalías con IA (reporte r116).

  Un renglón por variable de sensor con su estado, su tendencia y la fecha
  estimada del PRÓXIMO evento de desviación, antes de que ocurra. Este
  componente no calcula nada: el análisis vive en PredictivoAnomaliasService
  (backend) y aquí solo se pinta lo que devuelve /api/PredictivoAnomalias.
  Cada instancia muestra su propia planta.

  Botones por renglón:
    · Pronóstico — histórico diario + proyección por tendencia (gráfica).
    · Hoy        — comportamiento del día en curso por minuto (gráfica).
    · Rangos     — rango automático (IA) vs rango manual (plataforma), con el
                   selector de modo AUTO/MANUAL por variable.

  En AUTO el renglón se evalúa con el rango calculado por IA a partir del
  comportamiento reciente; en MANUAL con el rango capturado en la plataforma
  (Configuración → Máquinas y herramientas → "Configurar rangos").
-->
<template>
  <v-col cols="12" class="pa-0">
    <div id="predictivo-anomalias">
      <!-- Encabezado del reporte: título, hora del último cálculo y refresco manual. -->
      <div class="pa-head">
        <div>
          <h1>{{ $t('Reports.PredictiveAnomalies.title') }}</h1>
          <div class="sub">
            {{ $t('Reports.PredictiveAnomalies.subtitle', { hora: val(tablero, 'actualizado', 'Actualizado') || '—' }) }}
          </div>
        </div>
        <div class="der">
          <span v-if="enlaceTexto" :class="['enlace', enlaceVivo ? 'vivo' : 'caido']">{{ enlaceTexto }}</span>
          <span v-if="enDesviacion !== null" class="kpi">{{ $t('Reports.PredictiveAnomalies.inDeviation', { n: enDesviacion }) }}</span>
          <button class="claro" :disabled="cargando" @click="refrescar">{{ $t('Reports.PredictiveAnomalies.refresh') }}</button>
          <!-- Selector de idioma: mismo mecanismo que Seguimiento de alertas y Monitoreo de sensores (i18n global + localStorage 'locale'). -->
          <v-tooltip bottom color="blue-grey darken-4">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" class="str-language-control" v-on="on">
                <v-icon small color="primary" class="mr-2">mdi-translate</v-icon>
                <v-btn-toggle :value="currentLocale" mandatory dense class="str-language-toggle" @change="setLocale">
                  <v-btn small value="es" class="str-language-btn"
                    :class="{ 'str-language-btn--active': currentLocale === 'es' }"
                    :aria-label="$t('Reports.PredictiveAnomalies.language.spanish')">
                    ES
                  </v-btn>
                  <v-btn small value="en" class="str-language-btn"
                    :class="{ 'str-language-btn--active': currentLocale === 'en' }"
                    :aria-label="$t('Reports.PredictiveAnomalies.language.english')">
                    EN
                  </v-btn>
                </v-btn-toggle>
              </div>
            </template>
            <span class="white--text">{{ $t('Reports.PredictiveAnomalies.language.tooltip') }}</span>
          </v-tooltip>
        </div>
      </div>

      <main>
        <div v-if="error" class="error-msg">{{ error }}</div>
        <div v-if="aviso" class="aviso-msg">{{ aviso }}</div>

        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="th">{{ $t('Reports.PredictiveAnomalies.table.state') }}</th>
                <th class="th">{{ $t('Reports.PredictiveAnomalies.table.variable') }}</th>
                <th class="th">{{ $t('Reports.PredictiveAnomalies.table.last') }}</th>
                <th class="th">{{ $t('Reports.PredictiveAnomalies.table.trend') }}</th>
                <th class="th">{{ $t('Reports.PredictiveAnomalies.table.next') }}</th>
                <th class="th centrado">{{ $t('Reports.PredictiveAnomalies.table.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="f in filas">
                <!-- Renglón principal de la variable. -->
                <tr :key="idDe(f)">
                  <td>
                    <v-chip small dark :color="colorEstado(estadoDe(f))">{{ etiquetaEstado(estadoDe(f)) }}</v-chip>
                  </td>
                  <td>
                    <div class="fw">{{ val(f, 'maquina', 'Maquina') }}</div>
                    <div class="mini">
                      {{ val(f, 'sensor', 'Sensor') }} · {{ val(f, 'variable', 'Variable') }}
                      <span class="mono">({{ val(f, 'clave', 'Clave') }})</span>
                    </div>
                  </td>
                  <td>
                    <div class="fw" :style="{ color: colorValor(estadoDe(f)) }">
                      {{ val(f, 'ultimoValor', 'UltimoValor') != null ? val(f, 'ultimoValor', 'UltimoValor') + unidadDe(f) : '—' }}
                    </div>
                    <!-- Valor convertido con una escala sin calibrar: se rotula «estimada», sin explicar cómo se convierte. -->
                    <div v-if="estimadoDe(f)" class="mini estimado">{{ textoEstimado(f) }}</div>
                    <div class="mini">
                      {{ val(f, 'minutosSinDato', 'MinutosSinDato') != null ? $t('Reports.PredictiveAnomalies.ago', { min: val(f, 'minutosSinDato', 'MinutosSinDato') }) : '—' }}
                    </div>
                  </td>
                  <td>
                    <span v-if="tendenciaDe(f) != null" :style="{ color: tendenciaDe(f) > 0 ? '#b26a00' : '#1976d2' }">
                      {{ tendenciaDe(f) > 0 ? '▲ +' : '▼ ' }}{{ tendenciaDe(f) }}{{ unidadDe(f) }}{{ $t('Reports.PredictiveAnomalies.perDay') }}
                    </span>
                    <span v-else class="mini">—</span>
                  </td>
                  <td>
                    <div class="fw">{{ textoServidor(val(f, 'proximoEvento', 'ProximoEvento')) }}</div>
                    <div class="mini">{{ textoServidor(val(f, 'proximoEventoDetalle', 'ProximoEventoDetalle')) }}</div>
                  </td>
                  <td class="centrado acciones">
                    <button class="accion verde" @click="abrirSerie(f, 'pronostico')">{{ $t('Reports.PredictiveAnomalies.buttons.forecast') }}</button>
                    <button class="accion azul" @click="abrirSerie(f, 'hoy')">{{ $t('Reports.PredictiveAnomalies.buttons.today') }}</button>
                    <button :class="['accion', 'ambar', modoDe(f) === 'MANUAL' ? 'solido' : '']" @click="abrirRangos(f)">{{ $t('Reports.PredictiveAnomalies.buttons.ranges') }}</button>
                  </td>
                </tr>

                <!-- Panel expandible del renglón: gráfica o tarjetas de rangos. -->
                <tr v-if="abierto.id === idDe(f)" :key="idDe(f) + '-panel'" class="panel">
                  <td colspan="6">
                    <!-- Panel de rangos: automático (IA) vs manual (plataforma) + modo. -->
                    <div v-if="abierto.tipo === 'rangos'" class="pa-panel">
                      <div class="panel-head">
                        <strong>{{ $t('Reports.PredictiveAnomalies.ranges.title', { variable: val(f, 'variable', 'Variable'), clave: val(f, 'clave', 'Clave'), maquina: val(f, 'maquina', 'Maquina') }) }}</strong>
                        <button class="claro" @click="cerrarPanel">{{ $t('Reports.PredictiveAnomalies.buttons.close') }}</button>
                      </div>
                      <div class="rangos">
                        <div :class="['tarjeta', modoDe(f) !== 'MANUAL' ? 'activa' : '']">
                          <div class="tarjeta-head">
                            <b>{{ $t('Reports.PredictiveAnomalies.ranges.autoTitle') }}</b>
                            <span v-if="modoDe(f) !== 'MANUAL'" class="enuso">{{ $t('Reports.PredictiveAnomalies.ranges.inUse') }}</span>
                          </div>
                          <div v-if="rangoAutoDe(f)">
                            <div>{{ $t('Reports.PredictiveAnomalies.ranges.normal') }} <b class="mono">{{ textoRango(rangoAutoDe(f), 'normal') }}</b></div>
                            <div>{{ $t('Reports.PredictiveAnomalies.ranges.warning') }} <b class="mono">{{ textoRango(rangoAutoDe(f), 'advertencia') }}</b> <span class="mini">{{ $t('Reports.PredictiveAnomalies.ranges.beyond') }}</span></div>
                            <div class="mini nota">{{ $t('Reports.PredictiveAnomalies.ranges.frozen', { fecha: val(rangoAutoDe(f), 'fechaCalculo', 'FechaCalculo') || '—' }) }}</div>
                          </div>
                          <div v-else class="mini">{{ $t('Reports.PredictiveAnomalies.ranges.collecting') }}</div>
                          <div class="modo-barra">
                            <button class="accion verde" :disabled="recalculando" @click="recalcular(f)">
                              {{ recalculando ? $t('Reports.PredictiveAnomalies.buttons.recalculating') : (confirmandoRecalculo ? $t('Reports.PredictiveAnomalies.buttons.confirmRecalc') : $t('Reports.PredictiveAnomalies.buttons.recalculate')) }}
                            </button>
                            <span v-if="confirmandoRecalculo && !recalculando" class="mini">
                              {{ $t('Reports.PredictiveAnomalies.ranges.confirmHint') }}
                            </span>
                          </div>
                        </div>
                        <div :class="['tarjeta', modoDe(f) === 'MANUAL' ? 'activa' : '']">
                          <div class="tarjeta-head">
                            <b>{{ $t('Reports.PredictiveAnomalies.ranges.manualTitle') }}</b>
                            <span v-if="modoDe(f) === 'MANUAL'" class="enuso ambar-bg">{{ $t('Reports.PredictiveAnomalies.ranges.inUse') }}</span>
                          </div>
                          <div v-if="rangoManualDe(f)">
                            <div>{{ $t('Reports.PredictiveAnomalies.ranges.normal') }} <b class="mono">{{ textoRango(rangoManualDe(f), 'normal') }}</b></div>
                            <div>{{ $t('Reports.PredictiveAnomalies.ranges.warning') }} <b class="mono">{{ textoRango(rangoManualDe(f), 'advertencia') }}</b> <span class="mini">{{ $t('Reports.PredictiveAnomalies.ranges.beyond') }}</span></div>
                          </div>
                          <div v-else class="mini ambar-txt">{{ $t('Reports.PredictiveAnomalies.ranges.noManual') }}</div>
                          <div class="mini nota">{{ $t('Reports.PredictiveAnomalies.ranges.manualNote') }}</div>
                        </div>
                      </div>
                      <div class="modo-barra">
                        <strong>{{ $t('Reports.PredictiveAnomalies.ranges.modeLabel') }}</strong>
                        <label><input type="radio" value="AUTO" v-model="modoSel"> {{ $t('Reports.PredictiveAnomalies.ranges.modeAuto') }}</label>
                        <label><input type="radio" value="MANUAL" v-model="modoSel"> {{ $t('Reports.PredictiveAnomalies.ranges.modeManual') }}</label>
                        <button class="accion verde solido" :disabled="guardandoModo" @click="guardarModo(f)">
                          {{ guardandoModo ? $t('Reports.PredictiveAnomalies.buttons.saving') : $t('Reports.PredictiveAnomalies.buttons.apply') }}
                        </button>
                        <span v-if="modoGuardado" class="ok">{{ $t('Reports.PredictiveAnomalies.buttons.saved') }}</span>
                      </div>
                      <div class="mini nota">
                        {{ $t('Reports.PredictiveAnomalies.ranges.modeNote') }}
                      </div>
                    </div>

                    <!-- Panel de gráfica: pronóstico (histórico + proyección) u hoy (por minuto). -->
                    <div v-else class="pa-panel">
                      <div class="panel-head">
                        <strong>
                          {{ abierto.tipo === 'hoy' ? $t('Reports.PredictiveAnomalies.chart.titleToday') : $t('Reports.PredictiveAnomalies.chart.titleForecast') }}
                          — {{ val(f, 'variable', 'Variable') }} ({{ val(f, 'clave', 'Clave') }})
                          <span v-if="estimadoDe(f)" class="mini estimado">· {{ textoEstimado(f) }}</span>
                        </strong>
                        <button class="claro" @click="cerrarPanel">{{ $t('Reports.PredictiveAnomalies.buttons.closeChart') }}</button>
                      </div>
                      <div v-if="cargandoSerie" class="mini centrado" style="padding:24px">{{ $t('Reports.PredictiveAnomalies.chart.loading') }}</div>
                      <apexchart v-else :key="graficaKey" type="line" height="320" :options="chartOptions" :series="chartSeries" />
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </template>
        </v-simple-table>

        <div class="nota mini">
          {{ $t('Reports.PredictiveAnomalies.footer') }}
        </div>
      </main>
    </div>
  </v-col>
</template>

<script>
import axios from "axios";
import VueApexCharts from "vue-apexcharts";

export default {
  name: "ReportePredictivoAnomalias",
  components: { apexchart: VueApexCharts },
  props: {
    /** El dashboard marca active cuando esta pestaña es la visible. */
    active: { type: Boolean, default: true },
  },
  data() {
    return {
      /** Respuesta completa de /PredictivoAnomalias/Estado. */
      tablero: {},
      /** Mensaje de error visible cuando el backend no responde. */
      error: "",
      /** Aviso informativo (no es falla): el historial de la variable se está calculando. */
      aviso: "",
      /** Última serie recibida, para repintar la gráfica abierta si cambia el idioma. */
      ultimaSerie: null,
      /** true mientras hay una consulta del tablero en curso. */
      cargando: false,
      /** Panel abierto: id de la variable y tipo ('pronostico' | 'hoy' | 'rangos'). */
      abierto: { id: null, tipo: null },
      /** true mientras se descarga la serie de una gráfica. */
      cargandoSerie: false,
      /** Opciones y series de la gráfica abierta (ApexCharts). */
      chartOptions: {},
      chartSeries: [],
      /** Cambia para forzar el re-render de la gráfica al abrir otra. */
      graficaKey: 0,
      /** Modo elegido en el panel de rangos antes de Aplicar. */
      modoSel: "AUTO",
      guardandoModo: false,
      modoGuardado: false,
      /** true tras el primer clic en "Recalcular": el segundo clic confirma. */
      confirmandoRecalculo: false,
      /** true mientras el backend recalcula el rango automático. */
      recalculando: false,
      /** Timer del refresco automático (cada minuto con la pestaña activa). */
      timer: null,
    };
  },
  computed: {
    /** Idioma activo (i18n global, el mismo que usan Seguimiento de alertas y Monitoreo de sensores). */
    currentLocale() {
      return this.$i18n.locale;
    },
    /** Renglones del tablero tolerando ambas capitalizaciones del API. */
    filas() {
      return this.val(this.tablero, "filas", "Filas") || [];
    },
    /** Cuántas variables están fuera de su rango en este momento. */
    enDesviacion() {
      const v = this.val(this.tablero, "enDesviacion", "EnDesviacion");
      return v === undefined ? null : v;
    },
    /** true si la última lectura de la planta es de hace pocos minutos. */
    enlaceVivo() {
      return !!this.val(this.tablero, "enlaceVivo", "EnlaceVivo");
    },
    /** Texto del chip del enlace ("hace N min · vivo/caído"). */
    enlaceTexto() {
      const m = this.val(this.tablero, "enlaceMinutosSinDato", "EnlaceMinutosSinDato");
      if (m === null || m === undefined) return "";
      return this.$t("Reports.PredictiveAnomalies.link.text", {
        min: m,
        estado: this.$t("Reports.PredictiveAnomalies.link." + (this.enlaceVivo ? "live" : "down")),
      });
    },
  },
  watch: {
    /** Al cambiar de idioma se repinta la gráfica abierta (sus textos se arman al abrirla). */
    currentLocale() {
      if (this.ultimaSerie && this.abierto.tipo && this.abierto.tipo !== "rangos") {
        this.pintarGrafica(this.ultimaSerie.datos, this.ultimaSerie.tipo);
      }
    },
  },
  methods: {
    /** Cambia y recuerda el idioma (mismo mecanismo que los reportes hermanos). */
    setLocale(locale) {
      if (!locale) return;
      this.$i18n.locale = locale;
      localStorage.setItem("locale", locale);
    },

    /** Quita avisos y errores de la pantalla. */
    limpiarMensajes() {
      this.error = "";
      this.aviso = "";
    },

    /**
     * Si el 400 es el aviso de «historial calculándose» devuelve su texto en el idioma activo;
     * si no, null. Contrato nuevo: Fields = { codigo: "HISTORIAL_CALCULANDO", porcentaje, accion }.
     * Un back sin ese cambio no emite este aviso (su 400 es otro y conserva el prefijo de error).
     */
    textoEspera(cuerpo) {
      if (!cuerpo || typeof cuerpo !== "object") return null;
      const campos = this.val(cuerpo, "fields", "Fields");
      if (campos && typeof campos === "object" && this.val(campos, "codigo", "Codigo") === "HISTORIAL_CALCULANDO") {
        const pct = this.val(campos, "porcentaje", "Porcentaje");
        const accion = this.val(campos, "accion", "Accion");
        const clave = accion === "pronostico" ? "forecast" : (accion === "recalculo" ? "recalc" : "generic");
        const texto = this.$t("Reports.PredictiveAnomalies.waiting." + clave, { pct: pct === undefined || pct === null ? "" : pct });
        // Sin porcentaje se omite el paréntesis «( %)».
        return pct === undefined || pct === null ? texto.replace(/\s*\(\s*%\s*\)/, "") : texto;
      }
      return null;
    },

    /**
     * Muestra el resultado de una petición fallida: aviso de espera sin prefijo (no es falla) o
     * «<prefijo>: <mensaje del servidor>»; sin cuerpo queda el mensaje genérico de axios.
     */
    mostrarFalla(claveError, e) {
      const cuerpo = e && e.response && e.response.data;
      const espera = this.textoEspera(cuerpo);
      if (espera) {
        this.limpiarMensajes();
        this.aviso = espera;
        return;
      }
      this.aviso = "";
      const msg = this.val(cuerpo && typeof cuerpo === "object" ? cuerpo : null, "msg", "Msg");
      this.error = this.$t("Reports.PredictiveAnomalies.errors." + claveError) + ": " + (msg || (e && e.message) || e);
    },

    /**
     * Traduce al idioma activo los textos fijos que emite el servidor en español (próxima desviación y
     * detalle). Si un texto no coincide con ninguno conocido se muestra tal cual. Es un puente hasta que
     * el servidor mande códigos.
     */
    textoServidor(texto) {
      if (!texto || this.currentLocale === "es") return texto;
      const T = (k, p) => this.$t("Reports.PredictiveAnomalies.server." + k, p);
      let m;
      // Rótulo «Historial parcial (x %) — » antepuesto al texto de siempre: se traduce y el resto se traduce igual.
      if ((m = /^Historial parcial(?: \((\d+) %\))? — ([\s\S]*)$/.exec(texto))) {
        const pre = m[1] === undefined ? T("partialNoPct") : T("partial", { pct: m[1] });
        return pre + this.textoServidor(m[2]);
      }
      // Cola «calculado con historial parcial» del detalle (con o sin lo anterior separado por « · »).
      if ((m = /^(?:([\s\S]*?) · )?calculado con historial parcial$/.exec(texto))) {
        return (m[1] ? this.textoServidor(m[1]) + " · " : "") + T("partialDetail");
      }
      const nivel = (n) => {
        const m = String(n).toLowerCase();
        return m === "advertencia" || m === "alarma" ? T("level." + m) : n;
      };
      // El mes abreviado que emite el servidor en español se traduce (14-oct -> 14-Oct, 14-ene -> 14-Jan).
      const meses = { ene: "Jan", feb: "Feb", mar: "Mar", abr: "Apr", may: "May", jun: "Jun", jul: "Jul", ago: "Aug", sep: "Sep", oct: "Oct", nov: "Nov", dic: "Dec" };
      const fecha = (f) => {
        const d = /^(\d{1,2})-([A-Za-zñÑ]{3})$/.exec(f);
        return d && meses[d[2].toLowerCase()] ? d[1] + "-" + meses[d[2].toLowerCase()] : f;
      };
      const fijos = {
        "Sin rango en uso — captura el rango (o junta historial para el automático)": "noRange",
        "la última lectura ya está fuera del rango en uso": "deviationNowDetail",
        "Sin cruce proyectable — solo por evento": "noCross",
        "sin deriva hacia los límites; vigilar arranques y días atípicos": "noCrossDetail",
        "Sin señal del sensor — revisarlo en sitio; sin pronóstico posible": "noSignal",
        "No se pudo preparar el historial por ahora; se reintenta automáticamente. Si persiste, avisa a soporte.": "prepareFail",
        "Modo MANUAL sin rango capturado en la plataforma — capturarlo allá o volver a AUTO": "manualNoRange",
        "Recolectando historial para el rango automático — o captura uno manual en la plataforma": "collectingAuto",
      };
      // El detalle puede llevar « · contra rango <origen>» al final.
      let sufijo = "";
      let cuerpo = texto;
      const mo = /^(.*?)(?: · )?contra rango (automático \(IA\)|manual \(plataforma\))$/.exec(texto);
      if (mo) {
        const origen = T("origin." + (mo[2].indexOf("auto") === 0 ? "auto" : "manual"));
        cuerpo = mo[1];
        sufijo = cuerpo ? T("against", { origen: origen }) : T("againstOnly", { origen: origen });
      }
      if (!cuerpo) return sufijo;
      if (fijos[cuerpo]) return T(fijos[cuerpo]) + sufijo;
      if ((m = /^EN DESVIACIÓN AHORA \((\w+), (.*)\)$/.exec(cuerpo))) {
        const num = /^(-?[\d.]+)(.*)$/.exec(m[2]);
        return T("deviationNow", { nivel: nivel(m[1]), valor: num ? num[1] : m[2], unidad: num ? num[2] : "" }) + sufijo;
      }
      if ((m = /^(.+?) \(~(\d+) días\) → (\S+) ([<>]) (-?[\d.]+)(.*)$/.exec(cuerpo))) {
        return T("trendCross", { fecha: fecha(m[1]), dias: m[2], nivel: nivel(m[3]), dir: m[4], limite: m[5], unidad: m[6] }) + sufijo;
      }
      if ((m = /^tendencia (.+?)\/día, cruzando por picos$/.exec(cuerpo))) {
        return T("trendDetail", { tend: m[1] }) + sufijo;
      }
      if ((m = /^(.+?) ~(\d\d):00 → (\S+) \(por ciclo diario\)$/.exec(cuerpo))) {
        const dia = m[1] === "HOY" ? T("today") : (m[1] === "MAÑANA" ? T("tomorrow") : fecha(m[1]));
        return T("cycle", { dia: dia, hora: m[2], nivel: nivel(m[3]) }) + sufijo;
      }
      if ((m = /^el perfil horario alcanza (-?[\d.]+)(.*) a esa hora$/.exec(cuerpo))) {
        return T("cycleDetail", { valor: m[1], unidad: m[2] }) + sufijo;
      }
      if ((m = /^Calculando historial \((\d+) %\) — el pronóstico aparece al terminar$/.exec(cuerpo))) {
        return T("calculating", { pct: m[1] });
      }
      return texto;
    },

    /**
     * Lee un campo tolerando las dos capitalizaciones que entrega el API
     * (Newtonsoft serializa en PascalCase; algunos proxys lo pasan a camelCase).
     */
    val(objeto, campo, Campo) {
      if (!objeto) return undefined;
      return objeto[campo] !== undefined ? objeto[campo] : objeto[Campo];
    },

    /** Id de la variable del renglón (llave para paneles y series). */
    idDe(f) {
      return this.val(f, "idMaquinaSensorVariable", "IdMaquinaSensorVariable");
    },
    estadoDe(f) {
      return this.val(f, "estado", "Estado");
    },
    modoDe(f) {
      return this.val(f, "modo", "Modo") || "AUTO";
    },
    tendenciaDe(f) {
      const t = this.val(f, "tendenciaDia", "TendenciaDia");
      return t === undefined ? null : t;
    },
    rangoAutoDe(f) {
      return this.val(f, "rangoAutomatico", "RangoAutomatico");
    },
    rangoManualDe(f) {
      return this.val(f, "rangoManual", "RangoManual");
    },
    /** Abreviatura de la unidad de la variable ("" si el catálogo no la trae). */
    unidadDe(f) {
      return this.val(f, "unidad", "Unidad") || "";
    },
    /** true = el valor viene convertido con una escala que todavía no se calibró. */
    estimadoDe(f) {
      return !!this.val(f, "estimado", "Estimado");
    },
    /** Rótulo del valor estimado: de temperatura si la unidad es de grados, genérico si no. */
    textoEstimado(f) {
      return /°\s*[CF]/i.test(this.unidadDe(f))
        ? this.$t("Reports.PredictiveAnomalies.estimated.temperature")
        : this.$t("Reports.PredictiveAnomalies.estimated.value");
    },

    /** Texto "min – max" de la banda normal o de advertencia de un rango. */
    textoRango(rango, banda) {
      if (!rango) return "—";
      if (banda === "normal") {
        return this.val(rango, "normalMin", "NormalMin") + " – " + this.val(rango, "normalMax", "NormalMax");
      }
      const minimo = this.val(rango, "advertenciaMin", "AdvertenciaMin");
      const maximo = this.val(rango, "advertenciaMax", "AdvertenciaMax");
      return (minimo === null || minimo === undefined ? "—" : minimo) + " – " +
             (maximo === null || maximo === undefined ? "—" : maximo);
    },

    /** Etiqueta legible del estado del renglón. */
    etiquetaEstado(estado) {
      const conocidos = ["NORMAL", "ADVERTENCIA", "ALARMA", "SIN_SENAL", "SIN_RANGO"];
      return conocidos.indexOf(estado) >= 0 ? this.$t("Reports.PredictiveAnomalies.states." + estado) : (estado || "—");
    },
    /** Color Vuetify del chip de estado. */
    colorEstado(estado) {
      const colores = { NORMAL: "green", ADVERTENCIA: "orange darken-1", ALARMA: "red darken-1", SIN_SENAL: "blue-grey", SIN_RANGO: "grey" };
      return colores[estado] || "grey";
    },
    /** Color del valor de la última lectura según el estado. */
    colorValor(estado) {
      if (estado === "ALARMA") return "#c62828";
      if (estado === "ADVERTENCIA") return "#b26a00";
      return "#1c2b2a";
    },

    /** Descarga el tablero. Con silencioso=true no parpadea el botón (refresco del timer). */
    async cargar(silencioso) {
      if (!silencioso) this.cargando = true;
      try {
        const r = await axios.get("/PredictivoAnomalias/Estado");
        this.tablero = r.data || {};
        this.limpiarMensajes();
      } catch (e) {
        this.mostrarFalla("load", e);
      } finally {
        this.cargando = false;
      }
    },

    /** Botón "Actualizar ahora": recalcula en el backend ignorando el caché. */
    async refrescar() {
      this.cargando = true;
      try {
        const r = await axios.post("/PredictivoAnomalias/Refrescar", {});
        this.tablero = r.data || {};
        this.limpiarMensajes();
      } catch (e) {
        this.mostrarFalla("refresh", e);
      } finally {
        this.cargando = false;
      }
    },

    /** Cierra cualquier panel abierto. */
    cerrarPanel() {
      this.abierto = { id: null, tipo: null };
      this.confirmandoRecalculo = false;
    },

    /**
     * Abre (o cierra, si ya estaba abierta) la gráfica de un renglón:
     * 'pronostico' = histórico diario + proyección; 'hoy' = por minuto.
     */
    async abrirSerie(f, tipo) {
      const id = this.idDe(f);
      if (this.abierto.id === id && this.abierto.tipo === tipo) {
        this.cerrarPanel();
        return;
      }
      this.abierto = { id, tipo };
      this.cargandoSerie = true;
      try {
        const accion = tipo === "hoy" ? "SerieHoy" : "SeriePronostico";
        const r = await axios.get("/PredictivoAnomalias/" + accion + "/" + id);
        this.pintarGrafica(r.data || {}, tipo);
        this.limpiarMensajes();
      } catch (e) {
        this.mostrarFalla("chart", e);
        this.cerrarPanel();
      } finally {
        this.cargandoSerie = false;
      }
    },

    /** Convierte la respuesta de serie en opciones/series de ApexCharts. */
    pintarGrafica(datos, tipo) {
      this.ultimaSerie = { datos, tipo };
      // Umbrales del rango en uso como líneas punteadas sobre la gráfica.
      const rango = this.val(datos, "rango", "Rango");
      const anotaciones = [];
      const linea = (y, color, texto) => {
        if (y === null || y === undefined) return;
        anotaciones.push({
          y, borderColor: color, strokeDashArray: 6,
          label: { text: texto, style: { color: "#fff", background: color } },
        });
      };
      if (rango) {
        linea(this.val(rango, "normalMax", "NormalMax"), "#f9a825", this.$t("Reports.PredictiveAnomalies.chart.normalMax"));
        linea(this.val(rango, "normalMin", "NormalMin"), "#f9a825", this.$t("Reports.PredictiveAnomalies.chart.normalMin"));
        linea(this.val(rango, "advertenciaMax", "AdvertenciaMax"), "#c62828", this.$t("Reports.PredictiveAnomalies.chart.alarm"));
        linea(this.val(rango, "advertenciaMin", "AdvertenciaMin"), "#c62828", this.$t("Reports.PredictiveAnomalies.chart.alarm"));
      }

      const base = {
        chart: { animations: { enabled: false }, toolbar: { show: true } },
        yaxis: { decimalsInFloat: 2 },
        annotations: { yaxis: anotaciones },
        tooltip: { shared: true },
        legend: { show: true },
      };

      if (tipo === "hoy") {
        this.chartSeries = [{ name: this.$t("Reports.PredictiveAnomalies.chart.seriesValue"), data: this.val(datos, "valores", "Valores") || [] }];
        this.chartOptions = Object.assign({}, base, {
          stroke: { width: 2, curve: "straight" },
          colors: ["#1976d2"],
          xaxis: { categories: this.val(datos, "minutos", "Minutos") || [], tickAmount: 12, labels: { rotate: 0 } },
        });
      } else {
        this.chartSeries = [
          { name: this.$t("Reports.PredictiveAnomalies.chart.seriesHistory"), data: this.val(datos, "historico", "Historico") || [] },
          { name: this.$t("Reports.PredictiveAnomalies.chart.seriesProjection"), data: this.val(datos, "proyeccion", "Proyeccion") || [] },
        ];
        this.chartOptions = Object.assign({}, base, {
          stroke: { width: [2.5, 2.5], curve: "straight", dashArray: [0, 6] },
          colors: ["#1976d2", "#197F72"],
          xaxis: { categories: this.val(datos, "etiquetas", "Etiquetas") || [], tickAmount: 10, labels: { rotate: 0 } },
        });
      }
      this.graficaKey++;
    },

    /** Abre (o cierra) el panel de rangos del renglón y precarga su modo actual. */
    abrirRangos(f) {
      const id = this.idDe(f);
      if (this.abierto.id === id && this.abierto.tipo === "rangos") {
        this.cerrarPanel();
        return;
      }
      this.abierto = { id, tipo: "rangos" };
      this.modoSel = this.modoDe(f);
      this.modoGuardado = false;
      this.confirmandoRecalculo = false;
    },

    /**
     * Botón "Recalcular" de la tarjeta del rango automático: el primer clic arma
     * la confirmación en el propio botón y el segundo la ejecuta. El backend
     * cierra el rango congelado vigente e inserta el nuevo con los últimos 15
     * días; con historial insuficiente avisa y no toca nada.
     */
    async recalcular(f) {
      if (!this.confirmandoRecalculo) {
        this.confirmandoRecalculo = true;
        return;
      }
      this.recalculando = true;
      try {
        await axios.post("/PredictivoAnomalias/RecalcularRango", {
          idMaquinaSensorVariable: this.idDe(f),
        });
        await this.cargar(true);
        this.limpiarMensajes();
      } catch (e) {
        this.mostrarFalla("recalc", e);
      } finally {
        this.recalculando = false;
        this.confirmandoRecalculo = false;
      }
    },

    /** Botón Aplicar del panel de rangos: guarda el modo y recarga el tablero. */
    async guardarModo(f) {
      this.guardandoModo = true;
      this.modoGuardado = false;
      try {
        await axios.post("/PredictivoAnomalias/CambiarModo", {
          idMaquinaSensorVariable: this.idDe(f),
          modo: this.modoSel,
        });
        await this.cargar(true);
        this.modoGuardado = true;
        this.limpiarMensajes();
      } catch (e) {
        this.mostrarFalla("mode", e);
      } finally {
        this.guardandoModo = false;
      }
    },
  },
  mounted() {
    // Idioma recordado por el selector (igual que los reportes hermanos).
    const savedLocale = localStorage.getItem("locale");
    if (savedLocale) this.$i18n.locale = savedLocale;
    // Primera carga y refresco cada minuto mientras la pestaña esté activa.
    this.cargar();
    this.timer = setInterval(() => {
      if (this.active) this.cargar(true);
    }, 60000);
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
  },
};
</script>

<style scoped>
/* Paleta del reporte: teal de marca para lo estable, ámbar/rojo para desviaciones. */
#predictivo-anomalias { background: #f5f7f7; padding: 12px; }
.pa-head { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; background: linear-gradient(90deg, #e1f5ee, #ffffff); border-radius: 12px; margin-bottom: 12px; }
.pa-head h1 { font-size: 22px; color: #0d564f; margin: 0; }
.pa-head .sub { color: #5f6f6c; font-size: 13px; }
.pa-head .der { display: flex; align-items: center; gap: 12px; }
.enlace { font-size: 12px; font-weight: 600; padding: 3px 10px; border-radius: 10px; }
.enlace.vivo { background: #e5f5e9; color: #1b8a5a; }
.enlace.caido { background: #fdecea; color: #c62828; }
.kpi { font-size: 12px; font-weight: 600; color: #b26a00; }
.claro { border: 1px solid #c9d4d2; background: #fff; border-radius: 8px; padding: 5px 12px; cursor: pointer; font-size: 13px; }
.th { background: #0d564f; color: #fff !important; white-space: nowrap; }
.centrado { text-align: center; }
.fw { font-weight: 700; }
.mini { color: #888; font-size: 12px; }
.estimado { color: #8a6d00; font-style: italic; }
.mono { font-family: ui-monospace, Menlo, monospace; }
.acciones { white-space: nowrap; }
.accion { border-radius: 8px; padding: 4px 10px; margin: 0 2px; font-size: 12.5px; cursor: pointer; border: 1px solid; background: #fff; }
.accion.verde { color: #197f72; border-color: #197f72; }
.accion.azul { color: #1976d2; border-color: #1976d2; }
.accion.ambar { color: #b26a00; border-color: #b26a00; }
.accion.solido { background: #197f72; color: #fff; }
.accion.ambar.solido { background: #b26a00; color: #fff; }
.panel td { background: #fafcfc; }
.pa-panel { padding: 14px; }
.panel-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; color: #0d564f; }
.rangos { display: flex; gap: 12px; flex-wrap: wrap; }
.tarjeta { flex: 1 1 320px; border: 1px solid #d7e3e0; border-radius: 10px; padding: 12px 14px; background: #fff; }
.tarjeta.activa { border: 2px solid #197f72; background: #f2faf8; }
.tarjeta-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.enuso { font-size: 10px; font-weight: 700; color: #fff; background: #197f72; border-radius: 8px; padding: 2px 8px; }
.enuso.ambar-bg { background: #b26a00; }
.ambar-txt { color: #b26a00; }
.modo-barra { display: flex; align-items: center; gap: 14px; margin-top: 12px; flex-wrap: wrap; }
.modo-barra label { font-size: 13px; cursor: pointer; }
.ok { color: #1b8a5a; font-weight: 700; font-size: 13px; }
.nota { margin-top: 8px; }
.aviso-msg { background: #e8f1fb; color: #1565c0; border-radius: 8px; padding: 8px 12px; margin-bottom: 10px; font-size: 13px; }
.str-language-control { align-items: center; background: #ffffff; border: 1px solid rgba(25, 118, 210, 0.35); border-radius: 12px; box-shadow: 0 4px 14px rgba(25, 118, 210, 0.14); display: inline-flex; padding: 6px 8px; }
.str-language-toggle { border-radius: 10px; overflow: hidden; box-shadow: inset 0 0 0 1px rgba(25, 118, 210, 0.12); }
.str-language-toggle ::v-deep .v-btn { border-radius: 0 !important; border: none !important; margin: 0 !important; }
.str-language-btn { background: #eef4fd !important; color: #1565c0 !important; font-weight: 800; letter-spacing: .4px; min-width: 48px !important; transition: all .2s ease; }
.str-language-btn--active { background: linear-gradient(135deg, #1e88e5 0%, #1565c0 100%) !important; color: #ffffff !important; }
.error-msg { background: #fdecea; color: #c62828; border-radius: 8px; padding: 8px 12px; margin-bottom: 10px; font-size: 13px; }
</style>
